const constructGraphQuery = (data, isInbox) => {
  const conditions = data
    .map((group) => {
      const orConditions = group
        .map((condition) => {
          switch (condition.filterName) {
            case 'From':
              return isInbox
                ? `from/emailAddress/address eq '${condition.inputValue}'`
                : '';
            case 'Subject':
              return `contains(subject, '${condition.inputValue}')`;
            case 'Is':
              return condition.inputValue.toLowerCase() === 'read'
                ? 'isRead eq true'
                : 'isRead eq false';
            case 'Before':
              return `receivedDateTime lt ${new Date(condition.inputValue).toISOString()}`;
            case 'After':
              return `receivedDateTime gt ${new Date(condition.inputValue).toISOString()}`;
            default:
              return '';
          }
        })
        .filter(Boolean)
        .join(' or ');
      return `(${orConditions})`;
    })
    .filter(Boolean)
    .join(' and ');

  return conditions
    ? `(${conditions}) and hasAttachments eq true`
    : 'hasAttachments eq true';
};

const constructSentItemsSearchQuery = (data) => {
  const searchConditions = data.map((group) => {
    const orConditions = group
      .map((condition) => {
        if (condition.filterName === 'To') {
          return `"${condition.inputValue}"`;
        }
        return '';
      })
      .filter(Boolean)
      .join(' OR ');
    return `(${orConditions})`;
  });

  return searchConditions.length ? `${searchConditions}` : '';
};

const fetchAttachments = async (emailId, accessToken) => {
  try {
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/messages/${emailId}/attachments`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    const data = await response.json();
    return data.value || [];
  } catch (error) {
    console.error(`Error fetching attachments for email ${emailId}:`, error);
    return [];
  }
};

const fetchEmails = async (payload, accessToken) => {
  const inboxQuery = constructGraphQuery(payload, true);
  const sentItemsSearchQuery = constructSentItemsSearchQuery(payload);
  const emails = [];
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  const processEmails = async (emailList, isInbox) => {
    const processedEmails = await Promise.all(
      emailList.map(async (email) => {
        if (!isInbox) {
          const attachments = await fetchAttachments(email.id, accessToken);
          if (attachments.length > 0) {
            return { ...email, attachments };
          }
        } else if (email.hasAttachments) {
          return email;
        }
        return null;
      }),
    );
    emails.push(...processedEmails.filter(Boolean));
  };

  const fetchPage = async (nextPage, initialCall = false, isInbox = true) => {
    try {
      let requestUrl = `https://graph.microsoft.com/v1.0${nextPage}`;

      if (initialCall) {
        if (isInbox) {
          requestUrl += `?$expand=attachments&$filter=${encodeURIComponent(inboxQuery)}&$top=50`;
        } else if (sentItemsSearchQuery) {
          requestUrl += `?$search=${encodeURIComponent(sentItemsSearchQuery)}&$top=50`;
        } else {
          requestUrl += '?$top=50'; // Default query for sent items if no search query
        }
      }

      const response = await fetch(requestUrl, { headers });
      const data = await response.json();

      if (data?.value) {
        await processEmails(data.value, isInbox);
      }

      if (data['@odata.nextLink']) {
        await fetchPage(data['@odata.nextLink'], false, isInbox);
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
    }
  };

  await fetchPage('/me/messages', true, true);
  await fetchPage('/me/mailFolders/sentItems/messages', true, false);
  const uniqueEmails = Array.from(
    new Map(emails.map((email) => [email.id, email])).values(),
  );

  return uniqueEmails;
};

export default fetchEmails;
